import React, { createContext, useContext, useEffect, useState } from 'react';
import logo from '../../assets/logo.png';

// Piezas compartidas que replican el formato impreso "Historia Clínica Visual" de Vision Click.
export const GOLD = '#b59a4a';
export const INK = '#2b2f36';
export const FOLIO_RED = '#d2453b';

// Errores de validación por campo ({campo: mensaje}). Los campos que aparecen aquí se marcan con contorno rojo y asterisco.
export const ErroresHoja = createContext({});

// Tiñe el logo (negro) de dorado en un canvas: html2canvas no soporta CSS filter, así que el PDF también sale dorado.
export const useGoldLogo = () => {
    const [src, setSrc] = useState(logo);
    useEffect(() => {
        const img = new Image();
        img.onload = () => {
            try {
                const canvas = document.createElement('canvas');
                canvas.width = img.naturalWidth;
                canvas.height = img.naturalHeight;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);
                ctx.globalCompositeOperation = 'source-in';
                ctx.fillStyle = GOLD;
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                setSrc(canvas.toDataURL('image/png'));
            } catch (e) {
                setSrc(logo);
            }
        };
        img.src = logo;
    }, []);
    return src;
};

export const HojaEncabezado = ({ titulo = 'HISTORIA CLÍNICA VISUAL', folio, folioPlaceholder = '', onFolioChange, readOnly = false, style }) => {
    const goldLogo = useGoldLogo();
    return (
        <div className="hv-header" style={style}>
            <img src={goldLogo} alt="Vision Click" className="hv-header-logo" />
            <div className="hv-header-right">
                <div className="hv-brand">VISION CLICK</div>
                <div className="hv-title-row">
                    <span className="hv-title">{titulo}</span>
                    {folio !== undefined && (
                        <span className="hv-folio">
                            <span className="hv-folio-n">Nº</span>
                            {readOnly || !onFolioChange ? (
                                <span className="hv-folio-val">{folio || folioPlaceholder}</span>
                            ) : (
                                <input name="folio_number" value={folio} onChange={onFolioChange} placeholder={folioPlaceholder} title="Se asigna automáticamente si se deja vacío" className="hv-folio-val" />
                            )}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

const fechaLegible = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
    return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
};

// html2canvas dibuja mal los <input>/<textarea> (texto recortado, bordes por defecto). En la copia que se
// captura se reemplazan por <div> con el mismo estilo y el valor como texto plano.
const prepararParaImpresion = (root) => {
    root.querySelectorAll('.d-print-none').forEach(el => el.remove());
    root.querySelectorAll('.d-print-inline').forEach(el => { el.style.setProperty('display', 'inline', 'important'); });
    root.querySelectorAll('.hv-asterisco, .hv-asterisco-celda').forEach(el => el.remove());
    root.querySelectorAll('.hv-error').forEach(el => el.classList.remove('hv-error'));
    root.querySelectorAll('input, textarea, select').forEach(el => {
        const div = root.ownerDocument.createElement('div');
        div.className = `${el.className} hv-print-field${el.tagName === 'TEXTAREA' ? ' hv-print-multiline' : ''}`;
        const style = el.getAttribute('style');
        if (style) div.setAttribute('style', style);
        let valor = el.tagName === 'SELECT' ? (el.selectedOptions[0]?.text || '') : el.value;
        if (el.type === 'date') valor = fechaLegible(valor);
        if (!valor && el.name === 'folio_number') valor = el.placeholder;
        div.textContent = valor || ' ';
        el.replaceWith(div);
    });
};

// Exporta la hoja a PDF en UNA sola página A4: se captura completa y se escala para que quepa.
export const exportarHojaPDF = async (element, filename) => {
    const html2canvas = (await import('html2canvas')).default;
    const { jsPDF } = await import('jspdf');
    // Se captura una copia fuera del modal con scroll: así la hoja sale completa aunque el usuario haya desplazado la vista.
    const contenedor = document.createElement('div');
    contenedor.style.cssText = 'position:absolute;top:0;left:0;z-index:-9999;pointer-events:none;background:#fff;';
    const copia = element.cloneNode(true);
    copia.removeAttribute('id');
    copia.style.width = `${Math.max(element.offsetWidth, 820)}px`;
    copia.style.maxWidth = 'none';
    // cloneNode no copia lo escrito en los campos: se traslada antes de convertirlos en texto.
    const originales = element.querySelectorAll('input, textarea, select');
    copia.querySelectorAll('input, textarea, select').forEach((el, i) => {
        if (originales[i]) el.value = originales[i].value;
    });
    copia.classList.add('hv-printing');
    prepararParaImpresion(copia);
    contenedor.appendChild(copia);
    document.body.appendChild(contenedor);


    let canvas;
    try {
        canvas = await html2canvas(copia, {
            scale: 2,
            useCORS: true,
            backgroundColor: '#ffffff',
            scrollX: -window.scrollX,
            scrollY: -window.scrollY,
            windowWidth: 1200 // evita que se apliquen los estilos de pantalla angosta en la captura
        });
    } finally {
        document.body.removeChild(contenedor);
    }

    const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
    const margen = 6;
    const anchoDisp = pdf.internal.pageSize.getWidth() - margen * 2;
    const altoDisp = pdf.internal.pageSize.getHeight() - margen * 2;
    const escala = Math.min(anchoDisp / canvas.width, altoDisp / canvas.height);
    const ancho = canvas.width * escala;
    const alto = canvas.height * escala;
    pdf.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', (pdf.internal.pageSize.getWidth() - ancho) / 2, margen, ancho, alto);
    pdf.save(filename);
};

// Campo con línea inferior, como en la hoja impresa: "ETIQUETA: ________"
export const Linea = ({ label, name, value, onChange, readOnly, type = 'text', list, placeholder, style, inputStyle, labelStyle }) => {
    const error = useContext(ErroresHoja)[name];
    return (
    <div className={`hv-line${error ? ' hv-error' : ''}`} style={style} title={error || undefined}>
        {label && <span className="hv-label" style={labelStyle}>{label}</span>}
        {error && <span className="hv-asterisco">*</span>}
        {readOnly || !onChange ? (
            <span className="hv-input hv-static" style={inputStyle}>{value || ' '}</span>
        ) : (
            <input type={type} name={name} value={value || ''} onChange={onChange} list={list} placeholder={placeholder} className="hv-input" style={inputStyle} aria-invalid={!!error} />
        )}
    </div>
    );
};

const Celda = ({ name, value, onChange, readOnly, list, placeholder, strong }) => {
    const error = useContext(ErroresHoja)[name];
    const clase = `${strong ? ' hv-strong' : ''}${error ? ' hv-error' : ''}`;
    return (
        <>
            {readOnly || !onChange ? (
                <span className={`hv-cell-static${clase}`} title={error || undefined}>{value || ' '}</span>
            ) : (
                <input name={name} value={value || ''} onChange={onChange} list={list} placeholder={placeholder} className={`hv-cell-input${clase}`} title={error || undefined} aria-invalid={!!error} />
            )}
            {error && <span className="hv-asterisco-celda">*</span>}
        </>
    );
};

// Recuadro con la lista de errores. No sale en el PDF.
export const AvisoErrores = ({ errores }) => {
    const mensajes = [...new Set(Object.values(errores || {}))];
    if (!mensajes.length) return null;
    return (
        <div className="hv-error-banner d-print-none" data-html2canvas-ignore="true" role="alert">
            <strong>Revise los campos marcados con <span style={{ color: '#dc2626' }}>*</span>:</strong>
            <ul>{mensajes.map(m => <li key={m}>{m}</li>)}</ul>
        </div>
    );
};

export const RxFinalTabla = ({ data, onChange, readOnly }) => (
    <>
        <div className="hv-section-label">RX. FINAL</div>
        <table className="hv-grid hv-rx-final">
            <thead>
                <tr>
                    <th style={{ width: '13%' }}></th>
                    <th>ESFERA</th>
                    <th>CILINDRO</th>
                    <th>EJE</th>
                    <th>ADD</th>
                    <th>AVL</th>
                    <th>AVC</th>
                </tr>
            </thead>
            <tbody>
                {['od', 'oi'].map(ojo => (
                    <tr key={ojo}>
                        <td className="hv-eye">{ojo === 'od' ? 'O.D' : 'O.I'}</td>
                        <td><Celda name={`esfera_${ojo}`} value={data[`esfera_${ojo}`]} onChange={onChange} readOnly={readOnly} strong /></td>
                        <td><Celda name={`cilindro_${ojo}`} value={data[`cilindro_${ojo}`]} onChange={onChange} readOnly={readOnly} strong /></td>
                        <td><Celda name={`eje_${ojo}`} value={data[`eje_${ojo}`]} onChange={onChange} readOnly={readOnly} strong /></td>
                        <td><Celda name={`add_${ojo}`} value={data[`add_${ojo}`]} onChange={onChange} readOnly={readOnly} strong /></td>
                        <td><Celda name={`avl_${ojo}`} value={data[`avl_${ojo}`]} onChange={onChange} readOnly={readOnly} list="hv-av-list" /></td>
                        <td><Celda name={`avc_${ojo}`} value={data[`avc_${ojo}`]} onChange={onChange} readOnly={readOnly} list="hv-av-cerca-list" /></td>
                    </tr>
                ))}
            </tbody>
        </table>
    </>
);

export const EspecificacionesLentes = ({ data, onChange, readOnly, conObservaciones = true }) => (
    <div className="hv-specs">
        <div className="hv-specs-grid">
            <div>
                <Linea label="TIPO DE LENTES:" name="tipo_lentes" value={data.tipo_lentes} onChange={onChange} readOnly={readOnly} list="hv-lens-types-list" />
                <Linea label="COLOR:" name="color" value={data.color} onChange={onChange} readOnly={readOnly} />
                <Linea label="BIFOCAL:" name="bifocal" value={data.bifocal} onChange={onChange} readOnly={readOnly} />
            </div>
            <div>
                <Linea label="D.P." name="dp" value={data.dp} onChange={onChange} readOnly={readOnly} />
                <Linea label="ALT." name="alt" value={data.alt} onChange={onChange} readOnly={readOnly} />
                <Linea label="DIAM:" name="diam" value={data.diam} onChange={onChange} readOnly={readOnly} />
            </div>
        </div>
        <Linea label="TRATAMIENTO:" name="tratamiento" value={data.tratamiento} onChange={onChange} readOnly={readOnly} list="hv-treatments-list" />
        {conObservaciones && (
            readOnly || !onChange ? (
                <div className="hv-lined hv-static" style={{ minHeight: '44px' }}>{data.observaciones || ' '}</div>
            ) : (
                <textarea name="observaciones" value={data.observaciones || ''} onChange={onChange} rows={2} className="hv-lined" />
            )
        )}
    </div>
);

export const AV_OPTIONS = [
    '20/15', '20/20', '20/25', '20/30', '20/40', '20/50',
    '20/70', '20/100', '20/150', '20/200', '20/400',
    'Cuenta Dedos', 'Mov. Manos', 'Percep. Luz', 'No PL'
];
export const AV_CERCA_OPTIONS = ['20/20', '0.50M', '0.75M', '1.00M', '1.25M', '1.50M', '2.00M', 'J1', 'J2', 'J3', 'J4', 'J5', 'J6'];
export const LENS_TYPES = [
    'Monofocal', 'Bifocal Flat-Top', 'Bifocal Invisible',
    'Progresivo Digital', 'Progresivo Premium', 'Lente de Contacto Blando',
    'Lente de Contacto Tórico', 'Lente de Contacto RGP'
];
export const TREATMENTS = [
    'Antirreflejo Básico', 'Filtro Azul (Blue Block UV420)',
    'Fotocromático (Transitions)', 'Fotocromático + Blue Block',
    'Policarbonato Antirrayas', 'Alto Índice 1.67', 'Polarizado'
];

export const HojaDatalists = () => (
    <>
        <datalist id="hv-av-list">{AV_OPTIONS.map(o => <option key={o} value={o} />)}</datalist>
        <datalist id="hv-av-cerca-list">{AV_CERCA_OPTIONS.map(o => <option key={o} value={o} />)}</datalist>
        <datalist id="hv-lens-types-list">{LENS_TYPES.map(o => <option key={o} value={o} />)}</datalist>
        <datalist id="hv-treatments-list">{TREATMENTS.map(o => <option key={o} value={o} />)}</datalist>
    </>
);

// Estilos del formato impreso. Se inyectan una sola vez por hoja.
export const HojaEstilos = () => (
    <style>{`
        .hv-sheet { width: 100%; max-width: 860px; background: #fff; color: ${INK}; padding: 22px 24px 26px 24px; box-sizing: border-box;
            font-family: Arial, Helvetica, sans-serif; font-size: 11px; border: 1px solid #d1d5db; border-radius: 4px; box-shadow: 0 4px 20px rgba(0,0,0,0.06); }
        .hv-header { display: flex; align-items: flex-end; gap: 22px; padding: 0 4px 12px 4px; }
        .hv-header-logo { height: 78px; object-fit: contain; }
        .hv-header-right { flex: 1; display: flex; flex-direction: column; align-items: center; }
        .hv-brand { color: ${GOLD}; font-weight: 900; font-size: 40px; letter-spacing: 0.04em; line-height: 1; font-family: 'Arial Black', Arial, sans-serif; }
        .hv-title-row { display: flex; align-items: baseline; gap: 18px; margin-top: 8px; }
        .hv-title { font-family: 'Times New Roman', Georgia, serif; font-weight: 700; font-size: 16px; color: #222; }
        .hv-folio { display: inline-flex; align-items: baseline; gap: 6px; }
        .hv-folio-n { font-family: 'Times New Roman', Georgia, serif; font-weight: 700; font-size: 20px; color: #222; }
        .hv-folio-val::placeholder { color: ${FOLIO_RED}; opacity: 0.55; }
        .hv-folio-val { color: ${FOLIO_RED}; font-size: 24px; font-weight: 400; width: 96px; border: none; outline: none; background: transparent; padding: 0; font-family: Arial, sans-serif; border-radius: 0; }

        .hv-body { width: 100%; }
        .hv-frame { border: 1.3px solid #4b5563; padding: 10px 14px 12px 14px; min-width: 0; box-sizing: border-box; width: 100%; }

        .hv-line { display: flex; align-items: flex-end; gap: 4px; margin-bottom: 6px; min-width: 0; }
        .hv-label { font-size: 10.5px; text-transform: uppercase; white-space: nowrap; color: #333; }
        .hv-input { flex: 1; min-width: 0; border: none; border-bottom: 1px solid #555; outline: none; background: transparent;
            font-size: 11.5px; padding: 0 4px 1px; color: #111; font-family: inherit; height: 17px; line-height: 16px; border-radius: 0; }
        .hv-input:focus, .hv-cell-input:focus, .hv-lined:focus { background: #fdf8e8; }
        .hv-static { display: inline-block; white-space: nowrap; overflow: hidden; }
        .hv-lined { width: 100%; border: none; outline: none; resize: none; font-family: inherit; font-size: 11.5px; color: #111;
            line-height: 20px; padding: 0 4px; box-sizing: border-box; display: block; white-space: pre-wrap;
            background: repeating-linear-gradient(to bottom, transparent 0, transparent 19px, #555 19px, #555 20px); }

        .hv-top { border-bottom: 1.3px solid #4b5563; margin: 0 -14px 12px; padding: 0 14px 6px; }
        .hv-row-patient { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        .hv-row4 { display: grid; grid-template-columns: 1.1fr 0.85fr 1.15fr 0.9fr; gap: 8px; }
        .hv-row2 { display: grid; grid-template-columns: 1.7fr 1fr; gap: 6px; }
        .hv-row3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 6px; }

        .hv-grid { width: 100%; border-collapse: collapse; text-align: center; font-size: 10.5px; table-layout: fixed; }
        .hv-grid th, .hv-grid td { border: 1.3px solid #4b5563; padding: 0; height: 26px; }
        .hv-grid th { font-weight: 400; color: #333; }
        .hv-eye { font-size: 12px; text-align: left !important; padding-left: 14px !important; }
        .hv-cell-input, .hv-cell-static { width: 100%; height: 100%; min-height: 24px; border: none; outline: none; background: transparent; text-align: center;
            font-size: 12px; color: #111; font-family: inherit; display: flex; align-items: center; justify-content: center; box-sizing: border-box; border-radius: 0; padding: 0 2px; }
        .hv-strong { font-weight: 700; }

        .hv-av-wrap { border: 1.3px solid #4b5563; border-radius: 16px; overflow: hidden; }
        .hv-av-wrap .hv-grid { border-style: hidden; }

        .hv-mid { display: grid; grid-template-columns: 1fr 1fr; gap: 34px; margin-top: 8px; }
        .hv-sub { font-size: 10.5px; margin: 8px 0 6px; color: #333; }
        .hv-eye-line .hv-label { width: 30px; font-size: 11px; }
        .hv-add { display: flex; align-items: flex-start; gap: 6px; }
        .hv-add-box { width: 74px; height: 24px; border: 1.3px solid #4b5563; text-align: center; font-size: 12px; outline: none; background: transparent; border-radius: 0; padding: 0 4px; }
        .hv-lc-title { font-size: 10.5px; color: #333; }
        .hv-lc-arcs { display: grid; grid-template-columns: 1fr 1fr; justify-items: center; width: 170px; margin: 2px 0 8px 18px; }

        .hv-section-label { font-size: 10.5px; color: #333; margin: 6px 0 4px; }
        .hv-rx-final .hv-eye { font-weight: 700; font-size: 13px; }

        .hv-specs { margin-top: 10px; }
        .hv-specs-grid { display: grid; grid-template-columns: 1.6fr 1fr; gap: 12px; }
        .hv-sign { display: flex; justify-content: flex-end; margin-top: 14px; }
        .hv-sign-box { width: 300px; position: relative; }
        .hv-sign-caption { position: absolute; right: 30px; top: -12px; font-size: 9px; color: #333; }

        .hv-extra { margin: 14px 0 0 0; border: 1.3px solid #4b5563; padding: 8px 12px 10px; }
        .hv-extra-title { font-size: 10.5px; font-weight: 700; letter-spacing: 0.05em; margin-bottom: 8px; color: #333; }
        .hv-extra-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 24px; }
        .hv-hint { font-size: 10px; color: #92400e; background: #fffbeb; border: 1px solid #fde68a; border-radius: 4px; padding: 3px 6px; margin: 2px 0 6px; }

        /* Historia anulada: se conserva, con aviso y sello visibles también en el PDF */
        .hv-sheet { position: relative; }
        .hv-anulada-banner { margin: 0 0 12px 0; padding: 8px 12px; border: 2px solid #dc2626; border-radius: 6px; background: #fef2f2; color: #991b1b; font-size: 12px; }
        .hv-anulada-sello { position: absolute; top: 42%; left: 50%; transform: translate(-50%, -50%) rotate(-28deg); font-size: 120px; font-weight: 900;
            letter-spacing: 0.08em; color: rgba(220, 38, 38, 0.18); border: 10px solid rgba(220, 38, 38, 0.18); border-radius: 18px; padding: 0 30px;
            pointer-events: none; z-index: 2; white-space: nowrap; }

        /* Campos con error */
        .hv-grid td { position: relative; }
        .hv-line.hv-error { box-shadow: 0 0 0 1.5px #dc2626; border-radius: 3px; background: #fef2f2; }
        .hv-line.hv-error .hv-input { border-bottom-color: #dc2626; }
        .hv-cell-input.hv-error, .hv-cell-static.hv-error, .hv-add-box.hv-error { box-shadow: inset 0 0 0 2px #dc2626; background: #fef2f2; }
        .hv-asterisco { color: #dc2626; font-weight: 700; font-size: 14px; line-height: 14px; }
        .hv-asterisco-celda { position: absolute; top: 1px; right: 4px; color: #dc2626; font-weight: 700; font-size: 14px; line-height: 14px; pointer-events: none; }
        .hv-error-banner { margin: 0 0 12px 0; padding: 8px 12px; border: 1.5px solid #dc2626; border-radius: 6px; background: #fef2f2; color: #991b1b; font-size: 12px; }
        .hv-error-banner ul { margin: 4px 0 0; padding-left: 18px; }

        /* Captura para PDF: los campos se reemplazan por texto (ver prepararParaImpresion) */
        .hv-printing { box-shadow: none !important; border: none !important; border-radius: 0 !important; }
        .hv-print-field { overflow: hidden; white-space: nowrap; text-overflow: clip; }
        .hv-input.hv-print-field { display: block; height: auto; min-height: 17px; line-height: 16px; }
        .hv-print-multiline { white-space: pre-wrap; min-height: 40px; }
        .hv-add-box.hv-print-field { display: flex; align-items: center; justify-content: center; }
        .hv-folio-val.hv-print-field { display: inline-block; }

        @media (max-width: 720px) {
            .hv-extra, .hv-error-banner, .hv-anulada-banner { margin-left: 0; }
            .hv-anulada-sello { font-size: 64px; }
            .hv-extra-grid { grid-template-columns: 1fr; }
            .hv-sheet { padding: 14px 10px; }
            .hv-header { padding-left: 0; flex-wrap: wrap; }
            .hv-brand { font-size: 28px; }
            .hv-mid, .hv-specs-grid, .hv-row2, .hv-row3, .hv-row-patient, .hv-row4 { grid-template-columns: 1fr; gap: 6px; }
        }
    `}</style>
);
